import { FILE_TYPE } from "@home-health-notify/domain";
import CancelIcon from "@mui/icons-material/Cancel";
import { Typography } from "@mui/material";
import { useMediaUrlWithState, useTranslation } from "hhn-commons";
import { useEffect, useState } from "react";
import ReferralAttachmentItemImage from "../chatInfo/tabs/info/patient/ReferralAttachmentItemImage";
import {
  ReferralCaptionInput,
  ReferralFileInfo,
  ReferralFileItem,
  ReferralFilesContainer,
  ReferralFilesWrapper,
  ReferralMediaIcon,
  ReferralRemoveIcon,
} from "./AddPatientGroup.style";

type ReferralFilesPreviewProps = {
  files: any[];
  removeReferralFile: (fileParams: any) => void;
  onSetCaption: (fileName: string, caption: string) => void;
  fileCaptionMap: Map<string, string>;
};

const ReferralFilesPreview = ({
  files,
  removeReferralFile,
  onSetCaption,
  fileCaptionMap,
}: ReferralFilesPreviewProps) => {
  return (
    <>
      {files && files.length > 0 && (
        <ReferralFilesWrapper>
          <Typography variant="body1" data-testid="referral-files-heading">
            Referral Files
          </Typography>
          <ReferralFilesContainer>
            {files.map((fileParams: any, index: number) => (
              <ReferralItemPreview
                fileParams={fileParams}
                key={index}
                removeReferralFile={removeReferralFile}
                fileCaptionMap={fileCaptionMap}
                onSetCaption={onSetCaption}
              />
            ))}
          </ReferralFilesContainer>
        </ReferralFilesWrapper>
      )}
    </>
  );
};

type ReferralItemPreviewProps = {
  fileParams: any;
  isEditable?: boolean;
  removeReferralFile: (fileParams: any) => void;
  onSetCaption: (fileName: string, caption: string) => void;
  fileCaptionMap: Map<string, string>;
};

const ReferralItemPreview = ({
  fileParams,
  isEditable = true,
  removeReferralFile,
  onSetCaption,
  fileCaptionMap,
}: ReferralItemPreviewProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "patientGroup" });
  let fileSizeInKB = fileParams.fileSize
    ? (fileParams.fileSize / 1024).toFixed(2) + " KB"
    : "";

  const previewState = useMediaUrlWithState(
    fileParams.id ? fileParams.preview : undefined,
  );
  const getImageUrl = () => {
    if (fileParams.id) {
      return previewState.data?.signedUrl;
    } else {
      return fileParams.preview;
    }
  };

  let iconSrc =
    fileParams.fileType === FILE_TYPE.IMAGE
      ? getImageUrl()
      : fileParams.iconPath;

  const [caption, setCaption] = useState("");
  useEffect(() => {
    setCaption(
      fileCaptionMap.has(fileParams.fileName)
        ? (fileCaptionMap.get(fileParams.fileName) ?? "")
        : "", //defaultCaption()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileParams]);

  return (
    <ReferralFileItem data-testid="referral-file-item">
      <ReferralMediaIcon>
        <ReferralAttachmentItemImage type={fileParams.fileExt} url={iconSrc} />
      </ReferralMediaIcon>
      <ReferralFileInfo>
        {isEditable && (
          <ReferralCaptionInput
            data-testid="file-caption"
            placeholder={tNs("addCaption")}
            disabled={!isEditable}
            value={caption}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              let captionText = e.target.value;
              setCaption(captionText);
              onSetCaption(fileParams.fileName, captionText);
            }}
          />
        )}
        <Typography variant="caption" data-testid="referral-file-size">
          {fileParams.fileExt} - {fileSizeInKB}
        </Typography>
      </ReferralFileInfo>
      {isEditable && (
        <ReferralRemoveIcon
          aria-label="remove referral file"
          onClick={() => removeReferralFile(fileParams)}
          data-testid="remove-referral-file"
        >
          <CancelIcon />
        </ReferralRemoveIcon>
      )}
    </ReferralFileItem>
  );
};

export default ReferralFilesPreview;
